/**
 * Canonical team data.
 *
 * Teams are now defined per-edition under `server/editions/<year>.ts`.
 * This module re-exports the current edition's roster as `WC2026_TEAMS` /
 * `TEAMS` so existing imports keep working. When the next edition ships,
 * `CURRENT_EDITION` becomes (for example) 2030 and these constants
 * automatically point at the new 48-team list — including, critically,
 * the football-data.org `id` values used by the sync.
 *
 * Confederation, group letter and flag emoji are baked into the edition
 * file because football-data.org does not return them.
 */

import { CURRENT_TEAMS, EDITIONS, getEdition } from "./editions";
import type { EditionTeam } from "./editions";

/**
 * The current edition's 48-team roster. Kept under the historical
 * `WC2026_TEAMS` name so callers don't need to be touched every
 * tournament — but the contents update automatically when a new edition
 * is added to `server/editions/index.ts`.
 */
export const WC2026_TEAMS = CURRENT_TEAMS;

/** Alias kept for historical callers. */
export const TEAMS = WC2026_TEAMS;

/** Get the team list for a specific edition year. */
export function teamsForYear(year: number): ReadonlyArray<EditionTeam> {
  return getEdition(year).teams;
}

export const GROUPS = ["A","B","C","D","E","F","G","H","I","J","K","L"] as const;
export type GroupName = typeof GROUPS[number];

export function getTeamsByGroup() {
  const grouped: Record<string, EditionTeam[]> = {};
  for (const g of GROUPS) grouped[g] = [];
  for (const t of WC2026_TEAMS) {
    if (t.group in grouped) grouped[t.group].push(t);
  }
  return grouped;
}

/** All editions known to the app, current first. Re-exported for convenience. */
export { EDITIONS };
